import React, { useState, useEffect } from "react";
import {
  Button,
  Typography,
  Box,
  IconButton,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { makeStyles } from "@mui/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import classNames from "classnames";
import { useSnackbar } from "hooks/useSnackbar";
import { SimpleTextInput } from "components/FormTextInput";
import {
  ImageType,
  DEFAULT_IMAGE_DIMENSIONS,
  calculateAspectRatioDimensions,
} from "utils/imageConstants";

interface FilePickerProps {
  value?: any;
  onFileSelect: (
    file: File | null,
    dimensions?: {
      targetWidth: number;
      targetHeight: number;
      originalWidth: number;
      originalHeight: number;
    }
  ) => void;
  name: any;
  fileType?: string;
  showPreview?: boolean;
  label?: string;
  metaData?: any;
  selectedImgPreviewStyle?: any;
  strict?: boolean;
  imageType?: ImageType;
  enableDimensionControl?: boolean;
  [key: string]: any;
}

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexFlow: "row wrap",
    alignItems: "center",
    gap: 30,
    marginRight: 30,
  },
  buttonContainer: { display: "flex", flexFlow: "row wrap", gap: 4 },
  previewBox: {
    aspectRatio: "16/9",
    height: "200px",
    objectFit: "contain",
    marginTop: 2,
    [theme.breakpoints.down("sm")]: {},
  },
  reduceHeight: {
    // height: 200,
  },
}));

const defaultMessage = "No preview available";

const FilePicker = ({
  value,
  onFileSelect,
  name,
  fileType = "image/png, image/jpeg, image/jpg, image/heic, image/webp, image/svg",
  showPreview = true,
  label = "",
  metaData = null,
  selectedImgPreviewStyle,
  strict = false, // New prop for strict file type checking
  imageType,
  enableDimensionControl = false,
  ...props
}: FilePickerProps) => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState<string | undefined>();
  const [fileSupportMessage, setFileSupportMessage] = useState(defaultMessage);

  // Dimension control state
  const [originalWidth, setOriginalWidth] = useState<number>(0);
  const [originalHeight, setOriginalHeight] = useState<number>(0);
  const [targetWidth, setTargetWidth] = useState<number>(0);
  const [targetHeight, setTargetHeight] = useState<number>(0);
  const [scaleProportionally, setScaleProportionally] = useState<boolean>(true);
  const [showDimensionControls, setShowDimensionControls] =
    useState<boolean>(false);

  const onChange = (event: any) => {
    const file = event.target.files[0];

    if (!file) return;

    // Strict check for ZIP files when strict prop is true
    if (
      strict &&
      !(
        file.type === "application/zip" ||
        file.name.toLowerCase().endsWith(".zip")
      )
    ) {
      showSnackbar("Only ZIP files are allowed", "error");
      event.target.value = null;
      return;
    }

    setSelectedFile(file);

    // Handle image preview only for image files
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.src = reader.result as string;

        img.onload = () => {
          if (
            metaData &&
            (metaData?.height || metaData?.width) &&
            (img.height > metaData?.height || img.width > metaData?.width)
          ) {
            onFileSelect(null);
            setSelectedFile(null);
            showSnackbar(
              `Select a image with proper dimensions, size less than or equal to ${metaData?.width} x ${metaData?.height} pixels, but got ${img.width}x${img.height}`,
              "error"
            );
            return;
          }
          setPreview(reader.result as string);

          // Set dimension data if dimension control is enabled
          if (enableDimensionControl && imageType) {
            setOriginalWidth(img.width);
            setOriginalHeight(img.height);
            // Use original image dimensions instead of default dimensions
            setTargetWidth(img.width);
            setTargetHeight(img.height);
            setShowDimensionControls(true);

            // Call parent with dimension data - using original dimensions
            if (onFileSelect) {
              onFileSelect(file, {
                targetWidth: img.width,
                targetHeight: img.height,
                originalWidth: img.width,
                originalHeight: img.height,
              });
            }
          } else {
            // Call parent without dimension data
            if (onFileSelect) {
              onFileSelect(file);
            }
          }
        };
      };
      reader.readAsDataURL(file);
    } else if (
      strict &&
      (file.type === "application/zip" ||
        file.name.toLowerCase().endsWith(".zip"))
    ) {
      // For ZIP files in strict mode, show special message
      setPreview(null);
      setFileSupportMessage("ZIP file selected - contains multiple files");
      if (onFileSelect) {
        onFileSelect(file);
      }
    } else {
      // For non-image files (like xlsx, csv, etc.), call onFileSelect
      setPreview(null);
      setFileSupportMessage("File selected (no preview available)");
      if (onFileSelect) {
        onFileSelect(file);
      }
    }

    event.target.value = null;
  };

  const removeImage = () => {
    setFileSupportMessage(defaultMessage);
    setSelectedFile(null);
    setPreview(null);
    setShowDimensionControls(false);
    setOriginalWidth(0);
    setOriginalHeight(0);
    setTargetWidth(0);
    setTargetHeight(0);
    if (onFileSelect) {
      onFileSelect(null);
    }
  };

  const handleWidthChange = (e: any) => {
    const newWidth = Number(e.target.value);
    if (scaleProportionally && originalWidth && originalHeight) {
      const calculated = calculateAspectRatioDimensions(
        originalWidth,
        originalHeight,
        newWidth,
        "width"
      );
      setTargetWidth(calculated.width);
      setTargetHeight(calculated.height);
      // Notify parent
      if (onFileSelect && selectedFile) {
        onFileSelect(selectedFile, {
          targetWidth: calculated.width,
          targetHeight: calculated.height,
          originalWidth,
          originalHeight,
        });
      }
    } else {
      setTargetWidth(newWidth);
      // Notify parent
      if (onFileSelect && selectedFile) {
        onFileSelect(selectedFile, {
          targetWidth: newWidth,
          targetHeight,
          originalWidth,
          originalHeight,
        });
      }
    }
  };

  const handleHeightChange = (e: any) => {
    const newHeight = Number(e.target.value);
    if (scaleProportionally && originalWidth && originalHeight) {
      const calculated = calculateAspectRatioDimensions(
        originalWidth,
        originalHeight,
        newHeight,
        "height"
      );
      setTargetWidth(calculated.width);
      setTargetHeight(calculated.height);
      // Notify parent
      if (onFileSelect && selectedFile) {
        onFileSelect(selectedFile, {
          targetWidth: calculated.width,
          targetHeight: calculated.height,
          originalWidth,
          originalHeight,
        });
      }
    } else {
      setTargetHeight(newHeight);
      // Notify parent
      if (onFileSelect && selectedFile) {
        onFileSelect(selectedFile, {
          targetWidth,
          targetHeight: newHeight,
          originalWidth,
          originalHeight,
        });
      }
    }
  };

  const handleScaleToggle = (e: any) => {
    setScaleProportionally(e.target.checked);
  };

  return (
    <Box
      className={classNames(styles.container, {
        [styles.reduceHeight]: value,
      })}
    >
      <Box>
        <label style={{ fontSize: "1rem", fontWeight: "bold" }} htmlFor={name}>
          {label}
        </label>
        <Typography variant="body2">
          {value?.imageUrl
            ? `Selected Image: ${value.label}`
            : selectedFile?.name
            ? `Selected File: ` + selectedFile?.name
            : "No File Selected"}
        </Typography>
        <input
          type="file"
          id={name}
          accept={
            strict
              ? ".zip,application/zip,application/x-zip,application/x-zip-compressed"
              : fileType
          }
          style={{ display: "none" }}
          onChange={onChange}
        />
        <Box className={styles.buttonContainer}>
          <label htmlFor={name}>
            <Button
              sx={{
                width: 140,
              }}
              size="small"
              variant="contained"
              component="span"
              startIcon={<UploadFileIcon />}
            >
              Choose File
            </Button>
          </label>
          {selectedFile?.name && (
            <IconButton aria-label="delete" size="small" onClick={removeImage}>
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
        <Box>
          {metaData?.note && (
            <Typography variant="body2">
              {`Note: `}
              <i>{metaData?.note}</i>
            </Typography>
          )}
        </Box>

        {/* Dimension Controls */}
        {showDimensionControls && enableDimensionControl && imageType && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              border: "1px solid #ddd",
              borderRadius: 1,
              backgroundColor: "#f9f9f9",
            }}
          >
            <Typography
              variant="subtitle2"
              gutterBottom
              style={{ fontWeight: "bold" }}
            >
              Image Dimensions
            </Typography>

            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <SimpleTextInput
                label="Width (px)"
                name="targetWidth"
                type="number"
                value={targetWidth}
                onChange={handleWidthChange}
                inputProps={{ min: 10, max: 10000 }}
              />
              <SimpleTextInput
                label="Height (px)"
                name="targetHeight"
                type="number"
                value={targetHeight}
                onChange={handleHeightChange}
                inputProps={{ min: 10, max: 10000 }}
              />
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={scaleProportionally}
                  onChange={handleScaleToggle}
                />
              }
              label="Scale Proportionally"
            />

            <Box sx={{ mt: 1, fontSize: "0.85rem", color: "#666" }}>
              <Typography variant="caption" display="block">
                Original: {originalWidth} × {originalHeight}
              </Typography>
              <Typography variant="caption" display="block">
                Recommended: {DEFAULT_IMAGE_DIMENSIONS[imageType].width} ×{" "}
                {DEFAULT_IMAGE_DIMENSIONS[imageType].height}
              </Typography>
            </Box>

            {targetWidth !== DEFAULT_IMAGE_DIMENSIONS[imageType]?.width && (
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  bgcolor: "#fff3cd",
                  borderRadius: 1,
                  border: "1px solid #ffc107",
                }}
              >
                <Typography variant="caption" style={{ color: "#856404" }}>
                  ⚠️ Using custom dimensions (not recommended default)
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {(selectedFile || value?.imageUrl) && showPreview && (
        <>
          {preview || value?.imageUrl ? (
            <Box
              component="img"
              src={value?.imageUrl || preview}
              alt="Preview"
              className={styles.previewBox}
              style={selectedImgPreviewStyle}
            />
          ) : (
            <Typography variant="body2" color="textSecondary">
              {fileSupportMessage}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default FilePicker;
